import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, gr, eur, up } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";
import { toast } from "sonner";
import { Plus, SignIn, SignOut, Trash } from "@phosphor-icons/react";

const empty = { boat_name: "", owner: "", length: "", beam: "", draft: "", phone: "", slot_id: "", arrival: "", departure: "", daily_rate: "" };
const LABEL = { booked: "Κράτηση", checked_in: "Εντός", checked_out: "Αναχώρησε" };

export const TransientStays = ({ slots }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const { data: stays = [] } = useQuery({ queryKey: ["stays"], queryFn: async () => (await api.get("/stays")).data });

  const save = async () => {
    if (!form.boat_name.trim() || !form.owner.trim() || !form.slot_id || !form.arrival || !form.departure) {
      return toast.error("Συμπληρώστε σκάφος, ιδιοκτήτη, θέση και ημερομηνίες");
    }
    try {
      await api.post("/stays", {
        boat_name: form.boat_name.trim(), owner: form.owner.trim(), phone: form.phone.trim() || null,
        length: Number(form.length || 0), beam: form.beam === "" ? null : Number(form.beam),
        draft: form.draft === "" ? null : Number(form.draft),
        slot_id: form.slot_id, arrival: form.arrival, departure: form.departure,
        daily_rate: Number(form.daily_rate || 0),
      });
      await qc.invalidateQueries();
      setOpen(false); setForm(empty);
      toast.success("Καταχωρήθηκε");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/stays/${id}/${action}`);
      await qc.invalidateQueries();
      toast.success(action === "check-in" ? "Check-in" : "Check-out");
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
  };

  const del = async (id) => {
    try { await api.delete(`/stays/${id}`); await qc.invalidateQueries(); } catch { toast.error("Σφάλμα"); }
  };

  return (
    <Card className="rounded-sm" data-testid="transient-stays">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <div className="font-head text-base font-bold">Ημερήσιοι ελλιμενισμοί</div>
          <div className="text-xs text-muted-foreground">Κρατήσεις περαστικών σκαφών με check-in / check-out</div>
        </div>
        <Button size="sm" className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={() => setOpen(true)} data-testid="add-stay-btn">
          <Plus size={16} weight="bold" /> Νέα κράτηση
        </Button>
      </div>
      {stays.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-muted-foreground">Καμία κράτηση ημέρας.</div>
      ) : (
        <div className="divide-y divide-border">
          {stays.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm" data-testid={`stay-${s.id}`}>
              <div className="min-w-[180px] flex-1">
                <div className="font-medium">{s.boat_name} <span className="text-muted-foreground">· {s.length} m</span></div>
                <div className="text-xs text-muted-foreground">{s.owner} · θέση {s.slot_label || `#${s.slot_number}`}</div>
              </div>
              <div className="text-xs tabular-nums">{gr(s.arrival)} → {gr(s.departure)} <span className="text-muted-foreground">({s.nights} νύχτες)</span></div>
              <div className="w-24 text-right font-semibold tabular-nums">{eur(s.amount)}</div>
              <span className="rounded-sm bg-secondary px-2 py-0.5 text-xs font-semibold">{LABEL[s.status] || s.status}</span>
              {s.status === "booked" && (
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => act(s.id, "check-in")} data-testid={`stay-checkin-${s.id}`}>
                  <SignIn size={14} weight="bold" /> Check-in
                </Button>
              )}
              {s.status === "checked_in" && (
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => act(s.id, "check-out")} data-testid={`stay-checkout-${s.id}`}>
                  <SignOut size={14} weight="bold" /> Check-out
                </Button>
              )}
              <Button size="icon" variant="ghost" className="rounded-sm text-destructive" onClick={() => del(s.id)} data-testid={`stay-delete-${s.id}`}>
                <Trash size={16} />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader><DialogTitle className="font-head">Νέα κράτηση ημέρας</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Σκάφος</Label>
              <Input value={form.boat_name} onChange={(e) => setForm({ ...form, boat_name: up(e.target.value) })} className="mt-1 rounded-sm" data-testid="stay-boat-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Ιδιοκτήτης</Label>
              <Input value={form.owner} onChange={(e) => setForm({ ...form, owner: up(e.target.value) })} className="mt-1 rounded-sm" data-testid="stay-owner-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Μήκος (m)</Label>
              <Input type="number" value={form.length} onChange={(e) => setForm({ ...form, length: e.target.value })} className="mt-1 rounded-sm" data-testid="stay-length-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Τηλέφωνο</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" data-testid="stay-phone-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Πλάτος (m)</Label>
              <Input type="number" step="0.1" value={form.beam} onChange={(e) => setForm({ ...form, beam: e.target.value })} className="mt-1 rounded-sm" data-testid="stay-beam-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Βύθισμα (m)</Label>
              <Input type="number" step="0.1" value={form.draft} onChange={(e) => setForm({ ...form, draft: e.target.value })} className="mt-1 rounded-sm" data-testid="stay-draft-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-muted-foreground">Θέση</Label>
              <Select value={form.slot_id} onValueChange={(v) => setForm({ ...form, slot_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="stay-slot-select"><SelectValue placeholder="Επιλέξτε θέση" /></SelectTrigger>
                <SelectContent>
                  {slots.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.label || `Θέση ${s.number}`} · έως {s.max_length} m</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Άφιξη</Label>
              <DateField value={form.arrival} onChange={(v) => setForm({ ...form, arrival: v })} testId="stay-arrival" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Αναχώρηση</Label>
              <DateField value={form.departure} onChange={(v) => setForm({ ...form, departure: v })} testId="stay-departure" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Ημερήσια χρέωση (€)</Label>
              <Input type="number" step="0.01" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} className="mt-1 rounded-sm" data-testid="stay-rate-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Άκυρο</Button>
            <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={save} data-testid="stay-save-btn">Αποθήκευση</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
